
import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    siteType: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) body.append(key, value);
      });

      const res = await fetch('https://readdy.ai/api/form/d6ckh12ohb161tfd8430', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', siteType: '', budget: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-8 bg-gradient-to-br from-[#f5f3f0] via-white to-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-2 h-2 bg-[#1a2332] rotate-45"></div>
            <span className="text-sm font-medium text-[#1a2332] uppercase tracking-wider">Free Consultation</span>
          </div>
          <h1 className="text-5xl font-bold text-[#1a2332] mb-6">無料相談</h1>
          <p className="text-xl text-[#4a5568] leading-relaxed">
            サイト制作に関するご相談を無料で承ります。<br />
            まずはお気軽にお問い合わせください。内容を確認のうえ、折り返しご連絡いたします。
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Left Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#2d3748] mb-6">ご相談の流れ</h2>
                <div className="space-y-6">
                  {[
                    { step: '01', icon: 'ri-edit-line', title: 'フォーム送信', desc: '右のフォームからご相談内容をお送りください' },
                    { step: '02', icon: 'ri-mail-check-line', title: '内容確認', desc: '1〜2営業日以内にメールにてご連絡いたします' },
                    { step: '03', icon: 'ri-discuss-line', title: 'ヒアリング', desc: '必要に応じて詳細をお伺いします' },
                    { step: '04', icon: 'ri-file-list-3-line', title: 'お見積り', desc: 'ご要望に合わせたお見積りをご提示します' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1a2332] rounded-full flex-shrink-0">
                        <i className={`${item.icon} text-lg text-white`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#1a2332] mb-1">STEP {item.step}</p>
                        <h3 className="font-bold text-[#2d3748] mb-1">{item.title}</h3>
                        <p className="text-sm text-[#4a5568]">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#f5f3f0] p-6 rounded-xl">
                <h3 className="font-bold text-[#2d3748] mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#1a2332]"></i>
                  ご相談前にご確認ください
                </h3>
                <ul className="space-y-2 text-sm text-[#4a5568]">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                    <span>更新頻度の低いサイトに最適です</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                    <span>ドメイン費用は別途実費となります</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#1a2332] mt-0.5"></i>
                    <span>正式なお見積り後にご契約となります</span>
                  </li>
                </ul>
              </div>

              <div className="border border-[#e2e8f0] p-6 rounded-xl">
                <h3 className="font-bold text-[#2d3748] mb-3">メールでのお問い合わせ</h3>
                <a href="mailto:info@trim-page.com" className="text-[#1a2332] font-medium hover:text-[#4a5568] transition-colors flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  info@trim-page.com
                </a>
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3">
              {submitStatus === 'success' ? (
                <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#22c55e] rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#2d3748] mb-4">送信が完了しました</h3>
                  <p className="text-[#4a5568] mb-8">
                    お問い合わせありがとうございます。<br />
                    1〜2営業日以内にメールにてご連絡いたします。
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="px-8 py-3 bg-[#1a2332] text-white rounded-full font-medium hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    新しい相談を送る
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white border border-[#e2e8f0] rounded-xl p-8 space-y-6"
                >
                  <h2 className="text-2xl font-bold text-[#2d3748] mb-2">無料相談フォーム</h2>
                  <p className="text-sm text-[#4a5568] mb-6">
                    <span className="text-[#e53e3e]">*</span> は必須項目です
                  </p>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      お名前 <span className="text-[#e53e3e]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      メールアドレス <span className="text-[#e53e3e]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@email.com"
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="090-1234-5678"
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      会社名・屋号
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="株式会社〇〇 / 〇〇商店"
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors"
                    />
                  </div>

                  {/* Site Type */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      ご希望のサイト種別
                    </label>
                    <select
                      name="siteType"
                      value={formData.siteType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors bg-white cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="コーポレートサイト">コーポレートサイト</option>
                      <option value="店舗紹介サイト">店舗紹介サイト</option>
                      <option value="ポートフォリオ">ポートフォリオ</option>
                      <option value="ランディングページ">ランディングページ</option>
                      <option value="ブログ・メディア">ブログ・メディア</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      ご予算の目安
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors bg-white cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="3万円以下">3万円以下</option>
                      <option value="3万円〜5万円">3万円〜5万円</option>
                      <option value="5万円〜10万円">5万円〜10万円</option>
                      <option value="10万円以上">10万円以上</option>
                      <option value="未定・相談したい">未定・相談したい</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-bold text-[#2d3748] mb-2">
                      ご相談内容 <span className="text-[#e53e3e]">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="制作したいサイトの概要、ご要望、ご質問などをご記入ください"
                      className="w-full px-4 py-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a2332]/20 focus:border-[#1a2332] transition-colors resize-none"
                    />
                    <p className="text-xs text-[#4a5568] mt-1 text-right">
                      {formData.message.length} / 500文字
                    </p>
                  </div>

                  {/* Error */}
                  {submitStatus === 'error' && (
                    <div className="bg-[#fef2f2] border border-[#fecaca] rounded-lg p-4 flex items-center gap-3">
                      <i className="ri-error-warning-line text-xl text-[#e53e3e]"></i>
                      <p className="text-sm text-[#e53e3e]">送信に失敗しました。時間をおいて再度お試しください。</p>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-[#1a2332] text-white rounded-full font-medium hover:bg-[#2d3748] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        送信中...
                      </span>
                    ) : (
                      '無料相談を送信する'
                    )}
                  </button>

                  <p className="text-xs text-[#4a5568] text-center">
                    送信いただいた情報は
                    <Link to="/privacy-policy" className="underline hover:text-[#2d3748] cursor-pointer">プライバシーポリシー</Link>
                    に基づき適切に管理いたします。
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2332] text-white py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://public.readdy.ai/ai/img_res/4944a4ba-c62a-4964-bf85-4ed6714cc305.png"
                  alt="Trim-Page Logo"
                  className="w-10 h-10 object-contain"
                />
                <h3 className="text-3xl font-bold">Trim-Page</h3>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                小規模店舗・個人事業主のための、月額費用のない静的サイト制作サービス
              </p>
              <div className="flex gap-4">
                {['ri-twitter-x-line', 'ri-facebook-circle-line', 'ri-instagram-line', 'ri-linkedin-box-line'].map((icon, index) => (
                  <a
                    key={index}
                    href="#"
                    className="w-10 h-10 flex items-center justify-center border border-white/30 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <i className={`${icon} text-lg`}></i>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Contact</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white/60 mb-1">メールアドレス</p>
                  <a href="mailto:info@trim-page.com" className="text-white hover:text-white/80 transition-colors">
                    info@trim-page.com
                  </a>
                </div>
                <div>
                  <Link to="/contact" className="text-white underline hover:text-white/80 transition-colors cursor-pointer">
                    無料相談フォーム
                  </Link>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-6">Information</h4>
              <div className="space-y-4">
                <Link to="/about-operator" className="block text-white hover:text-white/80 transition-colors">
                  運営者情報
                </Link>
                <Link to="/tokushoho" className="block text-white hover:text-white/80 transition-colors">
                  特定商取引法に基づく表記
                </Link>
                <Link to="/privacy-policy" className="block text-white hover:text-white/80 transition-colors">
                  プライバシーポリシー
                </Link>
                <Link to="/support" className="block text-white hover:text-white/80 transition-colors">
                  サポートについて
                </Link>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/60">&copy; 2025 Trim-Page. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors">
                利用規約
              </a>
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors">
                サイトマップ
              </a>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
